import Cookies from "js-cookie";

/** How long auth + profile cookies survive across browser restarts. */
export const AUTH_COOKIE_DAYS = 365;

export const PERSISTENT_COOKIE_OPTIONS: Cookies.CookieAttributes = {
  expires: AUTH_COOKIE_DAYS,
  secure: true,
  sameSite: "Strict",
};

const PROFILE_KEYS = [
  "firstName",
  "lastName",
  "phoneNumber",
  "callingCode",
  "gender",
  "userId",
  "userRole",
  "appLanguage",
] as const;

export type ProfileCookieKey = (typeof PROFILE_KEYS)[number];

export interface UserProfile {
  firstName?: string;
  lastName?: string;
  phoneNumber?: string;
  callingCode?: string;
  gender?: string;
  userId?: string;
  userRole?: string;
  appLanguage?: string;
}

export function getAccessToken(): string | null {
  return Cookies.get("accessToken") || null;
}

export function getDeviceId(): string | null {
  return Cookies.get("deviceId") || null;
}

export function setDeviceId(deviceId: string): void {
  Cookies.set("deviceId", deviceId, PERSISTENT_COOKIE_OPTIONS);
}

export function setAccessToken(accessToken: string): void {
  Cookies.set("accessToken", accessToken, PERSISTENT_COOKIE_OPTIONS);
}

export function setAuthCookies({
  accessToken,
  refreshToken,
  deviceId,
}: {
  accessToken: string;
  refreshToken: string;
  deviceId: string;
}): void {
  setAccessToken(accessToken);
  Cookies.set("refreshToken", refreshToken, PERSISTENT_COOKIE_OPTIONS);
  setDeviceId(deviceId);
}

export function setUserProfile(profile: Partial<UserProfile>): void {
  const entries: [ProfileCookieKey, string | undefined][] = [
    ["firstName", profile.firstName],
    ["lastName", profile.lastName],
    ["phoneNumber", profile.phoneNumber],
    ["callingCode", profile.callingCode],
    ["gender", profile.gender],
    ["userId", profile.userId],
    ["userRole", profile.userRole],
    ["appLanguage", profile.appLanguage],
  ];

  for (const [key, value] of entries) {
    if (value != null && value !== "") {
      Cookies.set(key, value, PERSISTENT_COOKIE_OPTIONS);
    }
  }
}

export function getUserProfile(): UserProfile {
  return {
    firstName: Cookies.get("firstName"),
    lastName: Cookies.get("lastName"),
    phoneNumber: Cookies.get("phoneNumber"),
    callingCode: Cookies.get("callingCode"),
    gender: Cookies.get("gender"),
    userId: Cookies.get("userId"),
    userRole: Cookies.get("userRole"),
    appLanguage: Cookies.get("appLanguage"),
  };
}

/** Full name from profile cookies, e.g. "Mohan Kumar". */
export function formatUserDisplayName(profile?: UserProfile): string {
  const p = profile ?? getUserProfile();
  return [p.firstName, p.lastName].filter(Boolean).join(" ").trim();
}

export function hasAuthSession(): boolean {
  return Boolean(getAccessToken());
}

/** Removes auth tokens and persisted profile cookies (logout / account deletion). */
export function clearAuthSession(): void {
  Cookies.remove("accessToken");
  Cookies.remove("refreshToken");
  Cookies.remove("deviceId");

  for (const key of PROFILE_KEYS) {
    Cookies.remove(key);
  }
}

/** @deprecated Use `clearAuthSession` — kept for existing imports. */
export function clearAuthCookies(): void {
  clearAuthSession();
}
